/**
 * The in-memory half of the private preview cache shared by Documents and
 * Site Photos: a bounded fetch queue, in-flight de-duplication, an LRU of
 * object URLs, and a short memory of failures. The persistent half is
 * documentThumbnailStore. Everything is dropped on sign-out.
 */
const THUMBNAIL_CACHE_MAX = 200;
const MAX_CONCURRENT_THUMBNAILS = 6;
const FAILED_THUMBNAIL_TTL_MS = 5 * 60 * 1000;
const thumbnailCache = new Map<string, string>();
const failedThumbnailCache = new Map<string, number>();
const inflightThumbnails = new Map<string, Promise<string>>();
const thumbnailQueue: Array<() => void> = [];
let activeThumbnailFetches = 0;
let cacheGeneration = 0;
if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => {
  cacheGeneration += 1;
  for (const url of thumbnailCache.values()) URL.revokeObjectURL(url);
  thumbnailCache.clear(); failedThumbnailCache.clear(); inflightThumbnails.clear();
});

const enqueueThumbnailFetch = <T,>(task: () => Promise<T>) =>
  new Promise<T>((resolve, reject) => {
    const run = () => {
      activeThumbnailFetches += 1;
      task()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          activeThumbnailFetches -= 1;
          const next = thumbnailQueue.shift();
          if (next) next();
        });
    };

    if (activeThumbnailFetches < MAX_CONCURRENT_THUMBNAILS) {
      run();
    } else {
      thumbnailQueue.push(run);
    }
  });

const touchThumbnailCache = (cacheKey: string, url: string) => {
  if (thumbnailCache.has(cacheKey)) {
    thumbnailCache.delete(cacheKey);
  }
  thumbnailCache.set(cacheKey, url);

  if (thumbnailCache.size > THUMBNAIL_CACHE_MAX) {
    const oldest = thumbnailCache.keys().next().value;
    if (oldest) {
      const oldUrl = thumbnailCache.get(oldest);
      thumbnailCache.delete(oldest);
      if (oldUrl) {
        URL.revokeObjectURL(oldUrl);
      }
    }
  }
};

const hasRecentThumbnailFailure = (cacheKey: string) => {
  const failedAt = failedThumbnailCache.get(cacheKey);
  if (!failedAt) return false;
  if (Date.now() - failedAt > FAILED_THUMBNAIL_TTL_MS) {
    failedThumbnailCache.delete(cacheKey);
    return false;
  }
  return true;
};

/** A preview already in memory, so a remounted tile can paint on its first frame. */
export function peekThumbnailObjectUrl(cacheKey: string): string | undefined {
  const cached = thumbnailCache.get(cacheKey);
  if (cached) touchThumbnailCache(cacheKey, cached);
  return cached;
}

/** `fetchObjectUrl` runs at most once per key at a time, and must return an object URL this cache may revoke. */
export function loadThumbnailObjectUrl(cacheKey: string, fetchObjectUrl: () => Promise<string>): Promise<string> {
  const started = cacheGeneration;
  if (hasRecentThumbnailFailure(cacheKey)) {
    return Promise.reject(new Error('Recent thumbnail decode failure'));
  }

  const cached = peekThumbnailObjectUrl(cacheKey);
  if (cached) return Promise.resolve(cached);

  const existing = inflightThumbnails.get(cacheKey);
  if (existing) {
    return existing;
  }

  const promise = enqueueThumbnailFetch(async () => {
    try {
      if (started !== cacheGeneration) throw new Error('Preview session ended');
      const url = await fetchObjectUrl();
      if (started !== cacheGeneration) { URL.revokeObjectURL(url); throw new Error('Preview session ended'); }
      touchThumbnailCache(cacheKey, url);
      failedThumbnailCache.delete(cacheKey);
      return url;
    } catch (error) {
      if (started === cacheGeneration) failedThumbnailCache.set(cacheKey, Date.now());
      throw error;
    }
  }).finally(() => {
    if (started === cacheGeneration) inflightThumbnails.delete(cacheKey);
  });

  inflightThumbnails.set(cacheKey, promise);
  return promise;
}
